import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { SecondaryButton } from "../../StyledCss/styledbtn";
import LoadingView from "../LoadingView";
import FailureView from "../FailureView";
import { getPolicyRequests } from "../../api/services/hroneApi";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";

const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

const PolicyHistoryTab = ({ onView }) => {
  const { hasPermission } = usePermissions();
  const canView = hasPermission(FEATURE_CODES.POLICY_HISTORY_PDF_VIEW);
  const [historyData, setHistoryData] = useState([]);
  const [apiStatus, setApiStatus] = useState(apiStatusConstants.initial);

  const fetchHistory = useCallback(async () => {
    setApiStatus(apiStatusConstants.inProgress);
    try {
      const [approvedRes, rejectedRes, awaitingRes] = await Promise.all([
        getPolicyRequests("Approved"),
        getPolicyRequests("Rejected"),
        getPolicyRequests("Awaiting"),
      ]);

      if (approvedRes.ok && rejectedRes.ok && awaitingRes.ok) {
        const [approvedJson, rejectedJson, awaitingJson] = await Promise.all([
          approvedRes.json(),
          rejectedRes.json(),
          awaitingRes.json(),
        ]);

        const approvedData = approvedJson.status ? approvedJson.data : [];
        const rejectedData = rejectedJson.status ? rejectedJson.data : [];
        const awaitingData = awaitingJson.status ? awaitingJson.data : [];

        const combined = [...approvedData, ...rejectedData, ...awaitingData]
          .map((item) => convertKeysToCamelCase(item))
          .sort((a, b) => {
            const aAwaiting = a.status === "Awaiting" ? 0 : 1;
            const bAwaiting = b.status === "Awaiting" ? 0 : 1;
            if (aAwaiting !== bAwaiting) return aAwaiting - bAwaiting;
            return new Date(b.modifiedAt) - new Date(a.modifiedAt);
          });

        setHistoryData(combined);
        setApiStatus(apiStatusConstants.success);
      } else {
        setApiStatus(apiStatusConstants.failure);
      }
    } catch {
      setApiStatus(apiStatusConstants.failure);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    try {
      return format(new Date(dateStr), "dd-MMM-yyyy");
    } catch {
      return dateStr;
    }
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: "POLICY NAME",
      field: "policyName",
      filter: true,
      floatingFilter: true,
      suppressMenu: true,
      minWidth: 180,
    },
    {
      headerName: "UPLOADED BY",
      field: "hrName",
      filter: true,
      floatingFilter: true,
      suppressMenu: true,
      minWidth: 130,
    },
    {
      headerName: "STATUS",
      field: "status",
      filter: true,
      floatingFilter: true,
      suppressMenu: true,
      minWidth: 100,
    },
    {
      headerName: "ACTION BY",
      field: "adminName",
      filter: true,
      floatingFilter: true,
      suppressMenu: true,
      minWidth: 120,
    },
    {
      headerName: "DATE",
      field: "modifiedAt",
      filter: true,
      floatingFilter: true,
      suppressMenu: true,
      minWidth: 130,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "REJECT REASON",
      field: "rejectedReason",
      filter: true,
      floatingFilter: true,
      tooltipField: "rejectedReason",
      suppressMenu: true,
      minWidth: 150,
    },
    ...(canView
      ? [
          {
            headerName: "VIEW POLICY",
            suppressMenu: true,
            sortable: false,
            filter: false,
            minWidth: 120,
            cellRenderer: (params) => (
              <SecondaryButton
                className="d-flex justify-content-center align-items-center"
                onClick={() => params.data.policyUrl && onView(params.data)}
              >
                View
              </SecondaryButton>
            ),
          },
        ]
      : []),
  ], [onView, formatDate, canView]);

  const defaultColDef = useMemo(() => ({
    flex: 1,
    minWidth: 100,
    cellStyle: { fontSize: "14px" },
  }), []);

  const renderContent = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
        return <LoadingView />;
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={fetchHistory} />;
      case apiStatusConstants.success:
        return (
          <div
            className="ag-theme-alpine"
            style={{ height: "65vh", margin: "15px", fontSize: "14px" }}
          >
            <AgGridReact
              rowData={historyData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              suppressBrowserResizeObserver={true}
              pagination={true}
              paginationPageSize={20}
              enableCellTextSelection={true}
              alwaysShowVerticalScroll={true}
              overlayNoRowsTemplate="<span style='color:#757575;font-size:16px'>No policy history found</span>"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return renderContent();
};

export default PolicyHistoryTab;
